import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, Card, CardContent, CardHeader, Divider, FormHelperText, Grid, List, ListItem, ListItemButton, ListItemText, Stack, Tooltip, Typography, IconButton, ListItemIcon, Badge, useTheme } from "@mui/material";
import { crudGetList, HttpError, ReduxState, ResourceComponentPropsWithId, useGetMainList, useRecordContext, useDeleteWithConfirmController, useRedirect, useResourceContext } from "ra-core";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Content } from "@/components/content/Content";
import ReadonlyField from "@/components/input/ReadonlyField";
import { SearchAndAdd, SearchAndAddProps } from "@/components/SearchAndAdd";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { RecPatch } from "@/dataProvider/tools";
import { McpServerClientDto, SecureAIClientDto } from "@/gen/api/secureai";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import WithSave from '@/controller/WithSave';
import ViewLogsCard from '@/components/secureai/ViewLogsCard';
import Confirm from '@/components/widgets/Confirm';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';


const SecureAIAppDetail : FC<ResourceComponentPropsWithId> = (props) => {
  const { id, resource } = props
  useResourceRegister({ name: 'secure-ai-error-logs' })

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetList(resource ?? 'noop', pages(), sorts(), {}))
  }, [dispatch, resource])
  const appRec = useSelector<ReduxState, SecureAIClientDto|undefined>(store => {
    const data = store.admin.resources[resource ?? 'noop'].data
    return data[id ?? ''] as any
  })

  if (!appRec) { return null }
  return (
    <CreateOrEdit id={id} resource={resource} basePath="" record={appRec} >
      <SecureAIAppDetailBody {...props} />
    </CreateOrEdit>
  )
}


const SecureAIAppDetailBody : FC<ResourceComponentPropsWithId> = ({
  options, resource,
}) => {
  const appRec : SecureAIClientDto = useRecordContext() as any
  const format = useUserTimeFormat()
  const redirect = useRedirect()
  const baseResource = useResourceContext()

  const [term, setTerm] = useState('')
  const [states, setStates] = useState({
    stage: {
      removed: new Set<string>(),
      added: [] as McpServerClientDto[],
    },
    prevAssigned: new Set(appRec.assignedMcpServerIds ?? []),
    error: '',
    openDeleteConfirm: false,
  })

  const delHandlers = useDefaultSavedHandlers('Delete SecureAI App')
  delHandlers.onSuccess.merge(() => redirect('/secure-ai/dashboard'))

  const {
    open,
    loading: deleteLoading,
    handleDelete,
    handleDialogClose: handleDeleteDialogClose,
    handleDialogOpen: handleDeleteDialogOpen,
  } = useDeleteWithConfirmController({
    resource: baseResource ?? resource ?? 'secure-ai/app',
    record: appRec as any,
    ...delHandlers,
  })
  const { data, ids} = useGetMainList<McpServerClientDto&RecPatch>('secure-ai/mcp-servers', pages(), sorts(), {});
  const {mcpList, mcpMap} = useMemo(() => {
    const lsIds = (ids ?? []).filter(x => {
      const id: string = x as any  
      return states.stage.removed.has(id) || 
        (!states.prevAssigned.has(id) && !states.stage.added.some(x => x.clientId === id))
    })
    const mcpList = lsIds.map(id => (data ?? {})[id]).sort((a, b) => a.name!.toLowerCase() < b.name!.toLowerCase() ? -1 : 1)
    return {
      mcpList,
      mcpMap: data ?? {},
    }  
  }, [data, ids, states])

  const addValue = (x: McpServerClientDto | McpServerClientDto[]) => {
    setStates(v => {
      const ls = x instanceof Array ? x : [x]
      const arr = [] as McpServerClientDto[]
      let dirty = false
      for (const y of ls) {
        const id = y.clientId!
        if (v.stage.removed.has(id)) { 
          v.stage.removed.delete(id)
          dirty = true
          continue; 
        }
        if (v.prevAssigned.has(y.clientId!) 
          || v.stage.added.indexOf(y) > 0
        ) { continue; }
        arr.push(y)
      }
      return dirty || arr.length > 0 ? {...v, stage: {...v.stage, added: [...arr, ...v.stage.added]}} : v
    })
  }

  const dispatch = useDispatch()
  const theme = useTheme()
  const cardBgFix = theme.palette.mode === 'dark' && {
    sx: {
      bgcolor: '#121b25',
    }
  }

  const handleSave = (save: (x:string[])=>Promise<void>) => async () => {
    const assignedIds = [...states.prevAssigned, ...states.stage.added.map(a => a.clientId!)]
        .filter(id => !states.stage.removed.has(id))
    try {
      setStates(v => ({...v, error: ''}))
      await save(assignedIds)
      setStates(v => ({...v, stage: {removed: new Set<string>(), added: []}, prevAssigned: new Set(assignedIds)}))
      dispatch(crudGetList(resource ?? 'noop', pages(), sorts(), {}))
    } catch (error: any) {
      const hErr: HttpError = error
      setStates(v => ({...v, error: hErr.message}))
    }
  }
  
  return (
    <Content
      pageName="SecureAI App Details"
      resourceName="Details"
      breadcrumbs={options?.breadcrumbs}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card {...cardBgFix}>
            <CardHeader
              title={appRec.clientName}
              action={
                <Tooltip title="Delete SecureAI App">
                  <IconButton
                    sx={{
                      color: 'text.primary' as any,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={(e: any) => handleDeleteDialogOpen(e)}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <ReadonlyField label="Status" value={appRec.onlineStatus} />
                </Grid>
                <Grid item xs={6}>
                  <ReadonlyField label="Status Refresh Time" value={format(appRec.lastReportTime ?? 0, 'PPp')} />
                </Grid>
              </Grid>
              <Divider sx={{ mt: 4, mb: 2 }} />
              <Box>
                <Typography variant="subtitle2" marginBottom={2}>
                  Assigned Data Sources
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" >
                  <SearchAndAddMcpServer term={term} setTerm={setTerm} 
                    options={mcpList}
                    getOptionLabel={(x) => x.name!}
                    onAdd={addValue}
                  />
                  <Stack direction="row" spacing={1}>
                    <Typography variant="caption" color="primary">{appRec.assignedMcpServerIds?.length ?? 0}</Typography>
                    {states.stage.removed.size > 0 &&
                      <Typography variant="caption" color="error" fontWeight="bold" >- {states.stage.removed.size}</Typography>}
                    {states.stage.added.length > 0 &&
                      <Typography variant="caption" sx={{ color: theme => theme.palette.success.main }} fontWeight="bold">+ {states.stage.added.length}</Typography>}
                  </Stack>
                  <Box flex="1 1 auto" />
                  {(states.stage.removed.size > 0 || states.stage.added.length > 0) &&
                    <>
                      <Button variant="outlined" color="primary" size="small"
                        onClick={() => setStates(v => ({...v, stage: { removed: new Set<string>(), added: [] }, error: ''}))}
                      >
                        Cancel
                      </Button>
                      <WithSave async render={({save, saving}) => 
                        <Button variant="contained" color="primary" size="small"
                          disabled={saving}
                          onClick={handleSave(save)}
                        >
                          Save
                        </Button>
                      } />
                    </>}
                </Stack>
                {states.error && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>{states.error}</FormHelperText>
                  </Box>
                )}
                <Box maxHeight={400} overflow="auto">
                  <List sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', 
                    '& > li.MuiListItem-root': { 
                      flex: '0 0 50%', py: 0,
                      '& svg.MuiSvgIcon-root': { display: 'none' },
                      '&:hover svg.MuiSvgIcon-root': { display: 'block' },
                      '& div.MuiListItemText-root.removed': { textDecoration: 'line-through #ff000075' },
                      '& div.MuiListItemText-root.added': { color: theme => theme.palette.success.main },
                    }
                  }}>
                    {states.stage.added.map(x => {
                      const statusColor = x.onlineStatus && x.onlineStatus.toLowerCase() === 'online' ? 'success' : 'error'
                      return (
                        <ListItem>
                          <ListItemButton onClick={() => {
                            setStates(v => {
                              const i = v.stage.added.indexOf(x)
                              if (i > -1) {
                                v.stage.added.splice(i, 1)
                                return {...v}
                              }
                              return v
                            })
                          }}>
                            <ListItemIcon>
                              <Tooltip title={x.onlineStatus ?? ''}>
                                <Badge variant="dot" color={statusColor} sx={{ display: 'inline-flex' }} />
                              </Tooltip>
                            </ListItemIcon>
                            <ListItemText className="added">{x.name}</ListItemText>
                            <Tooltip title="Remove"><RemoveCircleIcon fontSize="small" color="error" /></Tooltip>
                          </ListItemButton>
                        </ListItem>)
                    })}
                    {(appRec.assignedMcpServerIds ?? []).flatMap(id => {
                      const mcp = mcpMap[id]
                      return !mcp ? [] : [mcp]
                    })
                    .sort((a, b) => a.name!.toLowerCase() < b.name!.toLowerCase() ? -1 : 1)
                    .map(mcp => {
                      const id = mcp.clientId!
                      const statusColor = mcp.onlineStatus && mcp.onlineStatus.toLowerCase() === 'online' ? 'success' : 'error'
                      return (
                        <ListItem>
                          <ListItemButton 
                            onClick={() => {
                              setStates(v => {
                                if (v.stage.removed.has(id)) {v.stage.removed.delete(id)}
                                else {v.stage.removed.add(id)}
                                return {...v}
                              })
                            }}>
                            <ListItemIcon>
                              <Tooltip title={mcp.onlineStatus ?? ''}>
                                <Badge variant="dot" color={statusColor} sx={{ display: 'inline-flex' }} />
                              </Tooltip>
                            </ListItemIcon>
                            <ListItemText className={states.stage.removed.has(id) ? 'removed' : undefined} >
                              {mcp?.name}
                            </ListItemText>
                            {states.stage.removed.has(id) 
                              ? <Tooltip title="Add back"><AddIcon fontSize="small" color="success" /></Tooltip>
                              : <Tooltip title="Remove"><RemoveCircleIcon fontSize="small" color="error" /></Tooltip>}
                          </ListItemButton>
                        </ListItem>)
                      }
                    )}
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} >
          <ViewLogsCard clientId={appRec.clientId!} />
        </Grid>
      </Grid>
      <Confirm
        isOpen={open}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onClose={(e: any) => {
          handleDeleteDialogClose(e);
          setStates(v => ({ ...v, openDeleteConfirm: false }));
        }}
        title="Delete SecureAI App"
        content={
          <Box>
            <Typography variant="body2">
              Are you sure you want to delete the SecureAI App "{appRec.clientName}"?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        }
        confirm="Delete"
        cancel="Cancel"
      />
    </Content>
  )
}

export default SecureAIAppDetail;

const SearchAndAddMcpServer : FC<SearchAndAddProps<McpServerClientDto>> = SearchAndAdd
